/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { Row, Col } from 'react-bootstrap'; // Import Panel from react-bootstrap
import useMedia from '../../../../../modules/react/hooks/useMedia';
import { Actions, Amenities, Price, Size, SizeWithUnitDetails, Specials } from '../columns';
import PanelComponent from '../../PanelComponent';
import { TRACK_CLICK_TIER_SELECTED } from '../../../../../src/web/utils/dataLayer';
import styles from './UnitTier.scss';
import themeOneStyles from '../themes/ThemeOne.scss';
import themeTwoStyles from '../themes/ThemeTwo.scss';
import AvailableUnitsCount from '../themes/AvailableUnitsCount';
import TierCard from './TierCard';
import useEventTrigger from '../../../../../modules/react/hooks/useEventTrigger';

const UnitTier = ({ group, facility, actionButtons, parentTheme, priceDisplay }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeButton, setActiveButton] = useState();
  const isMobile = useMedia(['(max-width:991px)'], [true], false);
  let { settings: { enableTieredPricing } } = facility;
  enableTieredPricing = enableTieredPricing && group.tiers?.length > 0;

  const mobileRef = useRef(null);
  const { addEvent } = useEventTrigger();


  useEffect(() => {
    if (mobileRef.current && isExpanded) {
      mobileRef.current.colSpan = 4;
    }
  }, [isExpanded]);

  const toggleTiers = (currentActiveButton) => {
    setIsExpanded(!isExpanded);
    setActiveButton(currentActiveButton);
  };

  const feeFilter = {
    facilityId: facility?.id,
    unitGroupId: group?.id,
    unitTierId: enableTieredPricing ? group?.minimumPriceTierId : null,
    softwareProvider: facility?.settings?.softwareProvider,
  };
  const actionProps = {
    ...group,
    facility,
    isModal: parentTheme === 'ThemeTwo' ? false : null,
    renderArrow: true,
    renderPriceInCircle: parentTheme === 'ThemeTwo' ? true : null,
    isUnitGroupExpanded: isExpanded,
    activeButton,
    actionButtons,
    parentTheme,
    toggleTiers,
    priceDisplay,
    isMobile,
    price: enableTieredPricing ? group.minimumUnitPrice : group.price,
    discountedPrice: enableTieredPricing ? group.minimumUnitDiscountedPrice : group.discountedPrice,
  };

  const getAnalyticsPayload = () => ({
    eventLocation: `unitsTable-${parentTheme}`,
    facilityId: facility?.id,
    timeStamp: Date.now(),
  });

  const [unitTierSelected, setTierSelected] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    const tierList = group.tiers.map(tier => tier.name);
    const defaultPayload = getAnalyticsPayload();
    const analyticsPayload = {
      tiersDisplayed: tierList,
      ...unitTierSelected,
      ...defaultPayload,
    };

    addEvent(TRACK_CLICK_TIER_SELECTED, analyticsPayload, 'click');
  }, [unitTierSelected]);

  const collapsedPane = (
    <PanelComponent
      isExpanded={isExpanded}
      panelClass={styles.unitGroupPanel}
      panelBodyClass={styles.tiersPanelBody}
      onToggle={toggleTiers}
    >
      <div className={styles.tiersContainer}>
        {group.tiers && group.tiers.filter(
          tier => !(!facility.settings?.showUnavailableUnits && tier?.availableUnitsCount === 0),
        ).sort((a, b) => a.rank - b.rank)
          .map(tier => (
            <TierCard
              key={tier.id}
              {...actionProps}
              setTierSelected={setTierSelected}
              feeFilter={feeFilter}
              tier={{ ...tier, unitGroupId: group.id }}
              enableTieredPricing={enableTieredPricing}
            />
          ))}
      </div>
    </PanelComponent>
  );

  let styleTiersEnabled = '';
  if (enableTieredPricing) {
    styleTiersEnabled = isExpanded ? styles.tierStyle : '';
  }

  return (
    <>
      {parentTheme === 'ThemeTwo' && (
        <>
          <div key={group.id} className={themeTwoStyles.groupTwo}>
            <Row className={themeTwoStyles.unitGroups}>
              <Col xs={3} sm={2} className={themeTwoStyles.priceBubble}>
                <div className={themeTwoStyles.price}>
                  <Price {...actionProps} feeFilter={feeFilter} />
                </div>
              </Col>
              <Col xs={6} sm={8} className={themeTwoStyles.sizeAmen}>
                <SizeWithUnitDetails {...actionProps} />
                <Row className={themeTwoStyles.specialsBotRow}>
                  <Col xs={10} className={`${themeTwoStyles.specialsTwoBot} col-xs-10`}>
                    <Specials {...actionProps} />
                  </Col>
                </Row>
              </Col>
              <Col xs={1} sm={2} className={themeTwoStyles.smallBtn}>
                <Actions
                  actionButtons={actionButtons}
                  actionContainerClassName={themeTwoStyles.actionButtons}
                  toggleTiers={toggleTiers}
                  enableTieredPricing={enableTieredPricing}
                  {...actionProps}
                />
              </Col>
            </Row>
            {enableTieredPricing && (
              <Row>
                {!isMobile && (
                  <Col
                    xs={3}
                    sm={2}
                    className={`${cx({
                      [styles.collapsiblePanelOpen]: isExpanded,
                      [styles.collapsiblePanel]: !isExpanded,
                    })} col-xs-3 col-sm-2`}
                  />
                )}
                <Col className={cx({
                  [styles.collapsiblePanelOpen]: isExpanded,
                  [styles.collapsiblePanel]: !isExpanded,
                })}
                >
                  {collapsedPane}
                </Col>
              </Row>
            )}
          </div>
        </>
      )}
      {parentTheme === 'ThemeOne' && (
        <>
          <tr key={group.id}>
            <Col as="td" xs={3} sm={2} className={`${cx(themeOneStyles.size, styleTiersEnabled)}`}>
              <div className={themeOneStyles.sizeWrapper}>
                <div className={themeOneStyles.sizeCentered}>
                  <Size {...actionProps} />
                </div>
                <AvailableUnitsCount {...actionProps} />
              </div>
            </Col>
            <Col
              as="td"
              xs={5}
              sm={6}
              className={`${cx(themeOneStyles.cellBordered, themeOneStyles.details, styleTiersEnabled)}`}
            >
              <div className="clearfix">
                <Amenities {...actionProps} />
              </div>
              <div className="clearfix">
                <Specials {...actionProps} />
              </div>
            </Col>
            <Col as="td" xs={3} sm={2} className={styleTiersEnabled}>
              <Price {...actionProps} feeFilter={feeFilter} />
            </Col>
            <Col as="td" xs={1} sm={2} className={styleTiersEnabled}>
              <Actions
                actionButtons={actionButtons}
                actionContainerClassName={themeOneStyles.actionButtons}
                toggleTiers={toggleTiers}
                enableTieredPricing={enableTieredPricing}
                {...actionProps}
              />
            </Col>
          </tr>

          {enableTieredPricing && (
            <>
              {!isMobile && (
                <tr>
                  <td
                    colSpan={1}
                    className={cx({
                      [styles.collapsiblePanelOpen]: isExpanded,
                      [styles.collapsiblePanel]: !isExpanded,
                    })}
                  />
                  <td
                    colSpan={3}
                    className={cx({
                      [styles.collapsiblePanelOpen]: isExpanded,
                      [styles.collapsiblePanel]: !isExpanded,
                    })}
                  >
                    {collapsedPane}
                  </td>
                </tr>
              )}
              {isMobile && (
                <tr>
                  <td
                    colSpan={4}
                    ref={mobileRef}
                    className={cx({
                      [styles.collapsiblePanelOpen]: isExpanded,
                      [styles.collapsiblePanel]: !isExpanded,
                    })}
                  >
                    {collapsedPane}
                  </td>
                </tr>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default UnitTier;
